import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import QRCode from 'qrcode';
import { Check, Circle, Mail, MapPin, Phone } from 'lucide-react';
import { PreArrivalView } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button, ConfirmButton, TextField } from '../../components/ui';
import { localToUtcIso } from '../../lib/zoneTime';
import { WalletButton } from '../public-p4/WalletButton';
import { getPre, preUrl, problemText } from './api';
import { PreArrivalExtras } from './PreArrivalExtras';
import { CarCard, Notices, PhotoCard, Questions, toField } from './PreArrivalForms';
import { LinkProblem, PageLoading, PublicShell, Section } from './PublicShell';

type Visit = PreArrivalView['visit'];

function when(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short', timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short' }).format(new Date(iso));
  }
}

const OPEN: Visit['status'][] = ['expected', 'pending_approval'];
const STATUS_TEXT: Partial<Record<Visit['status'], string>> = {
  pending_approval: 'publicLinks.pre.pending',
  cancelled: 'publicLinks.pre.cancelled',
  no_show: 'publicLinks.pre.noShow',
  rejected: 'publicLinks.pre.rejected',
  arrived: 'publicLinks.pre.arrived',
};

function Steps({ view }: { view: PreArrivalView }) {
  const p = view.progress;
  const steps = [
    { key: 'confirm', done: p.confirmed },
    ...(view.questions.some((q) => toField(q)) ? [{ key: 'questions', done: p.answersDone }] : []),
    ...(view.notices.length > 0 ? [{ key: 'notices', done: view.notices.every((n) => n.accepted) }] : []),
    ...(view.photo.wanted ? [{ key: 'photo', done: view.photo.done }] : []),
    ...(view.carRegistration.wanted ? [{ key: 'car', done: view.carRegistration.value !== null }] : []),
  ];
  const done = steps.filter((s) => s.done).length;
  return (
    <Section title={t('publicLinks.pre.progress')}>
      <p className="text-sm text-slate-700">{t('publicLinks.pre.progressCount', { done, total: steps.length })}</p>
      <ul className="space-y-2">
        {steps.map((s) => {
          const Icon = s.done ? Check : Circle;
          return (
            <li key={s.key} className="flex items-center gap-2">
              <Icon aria-hidden="true" className={`h-5 w-5 shrink-0 ${s.done ? 'text-green-800' : 'text-slate-400'}`} />
              <span className={s.done ? 'text-slate-600 line-through' : ''}>{t(`publicLinks.pre.step.${s.key}`)}</span>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function Where({ view }: { view: PreArrivalView }) {
  const s = view.site;
  const rows: [string, string][] = [
    [t('publicLinks.pre.where.address'), s.address],
    [t('publicLinks.pre.where.directions'), s.directions],
    [t('publicLinks.pre.where.parking'), s.parking],
  ].filter((r): r is [string, string] => (r[1] ?? '').trim() !== '');
  if (rows.length === 0 && !s.mapUrl && !s.wifi) return null;
  return (
    <Section title={t('publicLinks.pre.where.title')}>
      <dl className="space-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="font-medium">{k}</dt>
            <dd className="whitespace-pre-line text-slate-700">{v}</dd>
          </div>
        ))}
        {s.wifi ? (
          <div>
            <dt className="font-medium">{t('publicLinks.pre.where.wifi')}</dt>
            <dd className="text-slate-700">
              <div>{t('publicLinks.pre.where.wifiNetwork', { network: s.wifi.network })}</div>
              <div>{t('publicLinks.pre.where.wifiPassword', { password: s.wifi.password })}</div>
            </dd>
          </div>
        ) : null}
      </dl>
      {s.mapUrl ? (
        <a href={s.mapUrl} rel="noreferrer noopener" referrerPolicy="no-referrer" className="inline-flex min-h-11 items-center gap-2 text-brand underline">
          <MapPin aria-hidden="true" className="h-4 w-4" />
          {t('publicLinks.pre.where.map')}
        </a>
      ) : null}
    </Section>
  );
}

function Host({ view }: { view: PreArrivalView }) {
  const h = view.site.hostContact;
  if (!h) return null;
  return (
    <Section title={t('publicLinks.pre.host.title')}>
      <p className="font-medium">{h.name}</p>
      <div className="flex flex-wrap gap-3">
        {h.phone ? (
          <a href={`tel:${h.phone}`} className="inline-flex min-h-11 items-center gap-2 text-brand underline">
            <Phone aria-hidden="true" className="h-4 w-4" />
            {t('publicLinks.pre.host.call', { name: h.name })}
          </a>
        ) : null}
        {h.email ? (
          <a href={`mailto:${h.email}`} className="inline-flex min-h-11 items-center gap-2 text-brand underline">
            <Mail aria-hidden="true" className="h-4 w-4" />
            {t('publicLinks.pre.host.email', { name: h.name })}
          </a>
        ) : null}
      </div>
    </Section>
  );
}

/** The sign-in code is drawn in the browser from the payload; nothing is sent to a QR service. */
function Qr({ payload }: { payload: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(payload, { margin: 1, width: 220 })
      .then((d) => live && setSrc(d))
      .catch(() => live && setSrc(null));
    return () => {
      live = false;
    };
  }, [payload]);
  if (!src) return null;
  return (
    <Section title={t('publicLinks.pre.qr.title')}>
      <img src={src} alt={t('publicLinks.pre.qr.alt')} width={220} height={220} className="mx-auto" />
      <p className="text-center text-sm text-slate-700">{t('publicLinks.pre.qr.help')}</p>
    </Section>
  );
}

function Change({ view, send, pending, error }: { view: PreArrivalView; send: (part: string, body: unknown, method?: 'post' | 'put') => void; pending: boolean; error: string | null }) {
  const v = view.visit;
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [msg, setMsg] = useState('');
  const [bad, setBad] = useState(false);
  return (
    <>
      {v.canReschedule ? (
        <Section title={t('publicLinks.pre.reschedule.title')}>
          <div className="grid grid-cols-2 gap-3">
            <TextField label={t('publicLinks.pre.reschedule.date')} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <TextField label={t('publicLinks.pre.reschedule.time')} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <TextField label={t('publicLinks.pre.reschedule.message')} value={msg} maxLength={500} onChange={(e) => setMsg(e.target.value)} />
          {bad ? <p className="text-sm text-red-700">{t('publicLinks.pre.reschedule.invalid')}</p> : null}
          <Button
            disabled={pending}
            onClick={() => {
              if (!date || !time) return setBad(true);
              setBad(false);
              send('/reschedule', { expectedAt: localToUtcIso(date, time, v.timeZone), message: msg });
            }}
          >
            {t('publicLinks.pre.reschedule.button')}
          </Button>
        </Section>
      ) : null}
      {v.canCancel ? (
        <Section title={t('publicLinks.pre.cancel.title')}>
          <TextField label={t('publicLinks.pre.cancel.message')} value={msg} maxLength={500} onChange={(e) => setMsg(e.target.value)} />
          <ConfirmButton label={t('publicLinks.pre.cancel.button')} confirmLabel={t('publicLinks.pre.cancel.confirm')} pending={pending} onConfirm={() => send('/cancel', { message: msg })} />
        </Section>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </>
  );
}

/**
 * /p/:token (spec 8.6.7). Works with no sign-in: the signed link is the only credential, every call goes to
 * /pre-registration/public/:token, and the page shows only what that answer contains.
 */
export function PreArrivalPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const key = ['public-link', 'prearrival', token];
  const q = useQuery({ queryKey: key, queryFn: () => getPre(token), retry: false });
  const [which, setWhich] = useState<string | null>(null);

  // One mutation for every write: each answers with the updated page, which replaces the cached view.
  const write = useMutation({
    mutationFn: (a: { part: string; body?: unknown; form?: FormData; method?: 'post' | 'put'; area: string }) =>
      api[a.method ?? 'post'](preUrl(token, a.part), { schema: PreArrivalView, quiet401: true, ...(a.form ? { form: a.form } : { body: a.body }) }),
    onSuccess: (v) => qc.setQueryData(key, v),
  });
  const go = (area: string, part: string, body: unknown, method: 'post' | 'put' = 'post') => {
    setWhich(area);
    write.mutate({ part, body, method, area });
  };
  const errorFor = (area: string) => (write.isError && which === area ? problemText(write.error) : null);
  const busy = write.isPending;

  const v = q.data;
  const open = v ? OPEN.includes(v.visit.status) : false;
  const statusKey = v ? STATUS_TEXT[v.visit.status] : undefined;
  return (
    <PublicShell token={token} {...(v ? { title: t('publicLinks.pre.title', { site: v.site.name }) } : {})}>
      {q.isPending ? (
        <PageLoading />
      ) : q.isError || !v ? (
        <LinkProblem error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <Section>
            <p className="text-lg font-medium">{t('publicLinks.pre.hello', { name: v.visit.visitorName })}</p>
            <p>{t('publicLinks.pre.expected', { when: when(v.visit.expectedAt, v.visit.timeZone) })}</p>
            {v.visit.expectedUntil ? <p className="text-sm text-slate-700">{t('publicLinks.pre.expectedUntil', { when: when(v.visit.expectedUntil, v.visit.timeZone) })}</p> : null}
            {statusKey ? <Alert kind={v.visit.status === 'arrived' ? 'success' : 'info'}>{t(statusKey)}</Alert> : null}
            {open && !v.progress.confirmed ? (
              <>
                {errorFor('confirm') ? <Alert>{errorFor('confirm')}</Alert> : null}
                <Button variant="primary" className="w-full" disabled={busy} onClick={() => go('confirm', '/confirm', { confirmed: true })}>
                  {t('publicLinks.pre.confirm.button')}
                </Button>
              </>
            ) : null}
            {open && v.progress.confirmed ? <Alert kind="success">{t('publicLinks.pre.confirm.done')}</Alert> : null}
          </Section>

          {open ? (
            <>
              <Steps view={v} />
              <Qr payload={v.qrPayload} />
              <WalletButton token={token} />
              <Where view={v} />
              <Host view={v} />
              <Questions view={v} pending={busy} error={errorFor('answers')} onSave={(answers) => go('answers', '/answers', { answers }, 'put')} />
              <Notices view={v} pending={busy} error={errorFor('notices')} onAccept={(versionId, strokes) => go('notices', '/notices', { versionId, signatureStrokes: strokes })} />
              <PhotoCard
                view={v}
                pending={busy}
                error={errorFor('photo')}
                onUpload={(photo, thumb) => {
                  const form = new FormData();
                  form.append('photo', photo, 'photo.jpg');
                  form.append('thumb', thumb, 'thumb.jpg');
                  setWhich('photo');
                  write.mutate({ part: '/photo', form, area: 'photo' });
                }}
              />
              <PreArrivalExtras token={token} />
              <CarCard view={v} pending={busy} error={errorFor('car')} onSave={(registration) => go('car', '/car', { registration }, 'put')} />
              <Change view={v} pending={busy} error={errorFor('change')} send={(part, body) => go('change', part, body)} />
            </>
          ) : (
            <>
              <Where view={v} />
              <Host view={v} />
            </>
          )}
        </>
      )}
    </PublicShell>
  );
}
